import { useCallback, useState } from 'react';
import { caseApiService } from '../services/caseApiService';

export interface GuardedAction {
    editorName: string;
    run: () => void;
}

/**
 * ConcurrentCaseEditGuard awareness for blind case-list mutations (note, status): before the
 * change, live-check whether someone ELSE currently has the case open in the wizard. If so, defer
 * the action behind a "save anyway?" confirmation (guardedAction) instead of silently changing a
 * case another user is editing. The user can still proceed. The mutations are already guarded
 * server-side (row_version); this only adds the missing awareness. Fail-open on any check error.
 */
export function useCaseEditGuard(enabled: boolean, currentUserId: string | undefined) {
    const [guardedAction, setGuardedAction] = useState<GuardedAction | null>(null);

    const runWithEditGuard = useCallback(async (caseId: string | undefined, action: () => void | Promise<void>) => {
        const myId = currentUserId?.toLowerCase();
        if (enabled && caseId) {
            try {
                const status = await caseApiService.getEditingStatus(caseId);
                const editorId = status?.edby?.toLowerCase();
                if (editorId && editorId !== myId) {
                    const name = [status?.edbyfn, status?.edbyln].filter(Boolean).join(' ').trim() || 'Another user';
                    setGuardedAction({ editorName: name, run: () => { void action(); } });
                    return;
                }
            } catch {
                // fail-open: proceed as if no one is editing
            }
        }
        await action();
    }, [enabled, currentUserId]);

    return { guardedAction, setGuardedAction, runWithEditGuard };
}
